import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  inject,
  signal,
  QueryList,
  ViewChildren,
  DoCheck,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import locales from 'assets/locales.json';
import { variables } from 'libs/shared/utils/variables/variables';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { map } from 'rxjs';

import { AuthService } from '@offconon/core/features/auth';
import { FcmComponent } from '@offconon/shared/features/fcm';
import { LayoutLeftSidebarComponent } from '@offconon/shared/features/layout/layout-left-sidebar';
import { AppListRenderComponent } from '@offconon/shared/ui/app-list-render';
import { FeedbackCommonComponent } from '@offconon/shared/ui/feedback-common';
import { SubscriptionUpgradeDialogComponent } from '@offconon/shared/ui/subscription-upgrade-dialog';
import { ThemeService } from '@offconon/shared/ui/theme-switch';

import { LayoutService } from './data/services/layout.service';
import { setLeftColumnVisible, setRightColumnVisible } from './data/store/layout.actions';
import { selectLeftColumnVisible, selectRightColumnVisible } from './data/store/layout.selectors';

@Component({
  selector: 'offconon-layout',
  imports: [
    LayoutLeftSidebarComponent,
    RouterModule,
    DrawerModule,
    StyleClassModule,
    FcmComponent,
    CommonModule,
    ToastModule,
    CommonModule,
    NgClass,
    SubscriptionUpgradeDialogComponent,
    ButtonModule,
    MatIcon,
    TranslateModule,
    TooltipModule,
    PopoverModule,
    AppListRenderComponent,
    FeedbackCommonComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    ngSkipHydration: '',
  },
})
export class LayoutComponent implements OnInit, AfterViewInit, DoCheck {
  user: any;
  private router = inject(Router);
  private authenticationService = inject(AuthService);
  @Input() sideNavData: any = [];
  @Input() isLoggedIn = true;
  @Input() platform = 'core';
  @ViewChild('leftBottomContainer', { read: ViewContainerRef })
  leftBottomContainer: ViewContainerRef;

  @ViewChild('rightBottomContainer', { read: ViewContainerRef })
  rightBottomContainer: ViewContainerRef;

  @ViewChild('leftTopContainer', { read: ViewContainerRef })
  leftTopContainer: ViewContainerRef;

  @ViewChild('contentHeaderContainer', { read: ViewContainerRef })
  contentHeaderContainer: ViewContainerRef;
  @ViewChild('content') mainContent!: ElementRef;
  @ViewChildren('content') content!: QueryList<ElementRef>;
  @ViewChild('contentContainer') contentContainer!: any;

  private store = inject(Store);

  sideBarVisible = signal(false);

  themeService = inject(ThemeService);
  translate = inject(TranslateService);
  layoutService = inject(LayoutService);
  private breakpointObserver = inject(BreakpointObserver);

  leftColumnVisible = toSignal(this.store.select(selectLeftColumnVisible));
  rightColumnVisible = toSignal(this.store.select(selectRightColumnVisible));

  isMobileView = false;
  mobileView$ = this.breakpointObserver.observe(['(max-width: 960px)']).pipe(
    map((state) => {
      this.themeService.updatedMobileViewSelection(state.matches);
      this.isMobileView = state.matches;
      return state.matches;
    }),
  );

  mediumView$ = this.breakpointObserver.observe(['(max-width: 1280px)']).pipe(
    map((state) => {
      this.themeService.mediumView.set(state.matches);
      return state.matches;
    }),
  );
  currentLanguage = 'en';
  ngDoCheck(): void {
    if (this.content) {
      this.themeService.contentWidth.set(this.content?.last?.nativeElement?.scrollWidth);
    }
  }

  public ngAfterViewInit(): void {
    this.layoutService.setColumnRef(this.leftBottomContainer, 'leftBottom');
    this.layoutService.setColumnRef(this.rightBottomContainer, 'rightBottom');
    this.layoutService.setColumnRef(this.leftTopContainer, 'leftTop');

    this.layoutService.setColumnRef(this.contentHeaderContainer, 'contentHeader');

    this.setSideBar();

    if (this.mainContent) {
      this.layoutService.setContentCardNativeElement(this.mainContent.nativeElement);
    }
  }

  ngOnInit() {
    this.authenticationService.currentUserSubject.subscribe((user: any) => {
      this.user = user;
    });
    this.translate.addLangs(locales.locales);
    this.translate.setDefaultLang(variables.defaultLanguage);

    this.translate.onLangChange.subscribe((language) => {
      this.currentLanguage = language.lang;

      this.themeService.setRtlView();
    });
    this.themeService.setRtlView();

    this.mobileView$.subscribe({
      next: (isMobileView) => {
        if (isMobileView) {
          this.store.dispatch(setLeftColumnVisible({ leftColumnVisible: false }));
          this.store.dispatch(setRightColumnVisible({ rightColumnVisible: false }));
        }
      },
    });
    this.mediumView$.subscribe();
  }

  public setSideBar(state = false): void {
    this.sideBarVisible.set(state);
  }

  closeRightDrawer() {
    this.store.dispatch(setRightColumnVisible({ rightColumnVisible: false }));
  }
  toggleRightColumn() {
    this.store.dispatch(setRightColumnVisible({ rightColumnVisible: !this.rightColumnVisible()! }));
  }
  toggleLeftColumn() {
    this.store.dispatch(setLeftColumnVisible({ leftColumnVisible: !this.leftColumnVisible()! }));
  }
  openSubPage(url: any) {
    this.router.navigate([url]);
  }
  onScroll(event: Event) {
    this.layoutService.onMainContentScroll(event);
  }
}
